// Nihai Rapor paneli — spec §7.3. Review sayfasında sağ rail'in altında; tüm bölümler
// onaylandığında nihai DOCX üret/indir + mevcut doküman versiyonlarını listeler.
// DOCX üretimi/indirmesi YENİDEN YAZILMAZ — M7 documents endpoint'leri (ProjectDetail
// #018 ile aynı akış) reuse edilir: GET .../documents/export/full → {document,downloadUrl}
// → window.open. projects.download_docx yetkisi yoksa panel tamamen gizlenir.
import { useCallback, useEffect, useState } from "react";
import type { DocumentExportResponse, DocumentInfo, DocumentListResponse } from "@mahir/shared";
import { api, ApiError } from "../../api/client";
import { useAuthStore } from "../../stores/auth-store";
import { documentTypeLabel } from "../../lib/document-label";
import { REVIEW_COPY } from "../../lib/review-copy";

export function FinalReportPanel({
  projectId,
  approvedCount,
  totalCount,
}: {
  projectId: string;
  approvedCount: number;
  totalCount: number;
}): JSX.Element | null {
  const canDownload = useAuthStore((s) => s.user?.permissions["projects.download_docx"] === true);
  const [docs, setDocs] = useState<DocumentInfo[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allApproved = totalCount > 0 && approvedCount === totalCount;

  // GET default: superseded'lar hariç aktif doküman listesi (route includeSuperseded=false).
  const load = useCallback(async () => {
    try {
      const r = await api<DocumentListResponse>(`/projects/${projectId}/documents`);
      setDocs(r.documents.filter((d) => !d.supersededAt));
    } catch {
      /* yetki yoksa panel sessiz kalır */
    }
  }, [projectId]);
  useEffect(() => {
    if (canDownload) void load();
  }, [canDownload, load]);

  // Yetki yoksa panel hiç render edilmez (generate/download butonu gizli).
  if (!canDownload) return null;

  const exportFull = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<DocumentExportResponse>(`/projects/${projectId}/documents/export/full`);
      window.open(r.downloadUrl, "_blank");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Rapor üretilemedi");
    } finally {
      setBusy(false);
    }
  };

  // K6 — onaysız taslak ön izleme; üretilmiş bölüm (totalCount>0) varken hep açık
  const exportDraftPreview = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<DocumentExportResponse>(
        `/projects/${projectId}/documents/export/draft-preview`,
        { method: "POST" },
      );
      window.open(r.downloadUrl, "_blank");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : REVIEW_COPY.draftPreview.failed);
    } finally {
      setBusy(false);
    }
  };

  const pendingLabel = `${(totalCount - approvedCount).toString()} bölüm onay bekliyor`;

  return (
    <div className="mt-6">
      <h3
        className="mb-2 text-xs uppercase tracking-wider"
        style={{ color: "var(--color-text-3)" }}
      >
        📄 Nihai Rapor
      </h3>
      <button
        onClick={() => void exportFull()}
        disabled={!allApproved || busy}
        title={allApproved ? undefined : pendingLabel}
        className="w-full px-3 py-2 rounded-md text-sm disabled:opacity-50"
        style={{
          background: "var(--color-primary)",
          color: "var(--color-surface)",
          fontWeight: 600,
        }}
      >
        {busy ? "Üretiliyor…" : "Nihai Raporu Üret ve İndir"}
      </button>
      {!allApproved && (
        <p className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
          {pendingLabel}
        </p>
      )}
      <DraftPreviewButton
        enabled={totalCount > 0 && !busy}
        onClick={() => void exportDraftPreview()}
      />
      {error && (
        <p className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
          {error}
        </p>
      )}
      {docs.length > 0 && (
        <ul className="mt-2 text-xs flex flex-col gap-1">
          {docs.map((d) => (
            <li key={d.id} className="flex justify-between gap-2">
              <span>
                {documentTypeLabel(d)} · v{d.version} ·{" "}
                {new Date(d.generatedAt).toLocaleDateString("tr-TR")}
              </span>
              <button
                className="underline"
                onClick={() => {
                  void api<DocumentExportResponse>(`/projects/${projectId}/documents/${d.id}`).then(
                    (r) => {
                      window.open(r.downloadUrl, "_blank");
                    },
                  );
                }}
              >
                indir ({Math.round(d.fileSizeBytes / 1024)} KB)
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DraftPreviewButton({
  enabled,
  onClick,
}: {
  enabled: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <div className="mt-2">
      <button
        onClick={onClick}
        disabled={!enabled}
        title={REVIEW_COPY.draftPreview.hint}
        className="w-full px-3 py-2 rounded-md text-sm disabled:opacity-50"
        style={{
          background: "var(--color-surface)",
          color: "var(--color-text)",
          border: "1px solid var(--color-border)",
          fontWeight: 600,
        }}
      >
        {REVIEW_COPY.draftPreview.button}
      </button>
      <p className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
        {REVIEW_COPY.draftPreview.hint}
      </p>
    </div>
  );
}
